import { memo } from 'react';
import { Box, Table, TableBody, TableCell, TableRow } from '@mui/material';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Icons } from '../../../../../packages/icons';
import { colors } from '../../../../../packages/colors';
import { fontWeight, Heading, headingLevel, Text } from '../../../../../packages/ui/atoms/typography';
import { Badge, badgeStyle, badgeType } from '../../../../../packages/ui/atoms/badge';
import { getProfile } from '../../../../../redux/modules/profile/selectors';
import { useHistoryPatient } from '../../../../../hooks/useHistoryPatient';
import { useStyles } from '../../MemberDetail.styles';
import { useStyles as useHistoryStyles } from './History.styles';
import EmptyState from '../EmptyState';

const History = memo(() => {
  const classes = useStyles({});
  const historyCls = useHistoryStyles();
  const { memberId } = useParams();
  const { items, isLoading, error } = useHistoryPatient({ patientId: memberId });
  const { demographicDetails } = useSelector(getProfile);

  const renderCheckIcon = isChecked => (
    <Box>
      <Icons
        glyph="checkmark"
        color={isChecked ? colors.success500 : colors.destructive500}
        className={historyCls.icon}
      />
    </Box>
  );

  const renderContent = item => (
    <TableRow className={historyCls.row}>
      <TableCell>
        <Text className={historyCls.label}>{item.name}</Text>
      </TableCell>
      <TableCell>
        <Box className={historyCls.rowCell}>
          {item.checked !== undefined && renderCheckIcon(item.checked)}
          <Text className={historyCls.value}>{item.value}</Text>
        </Box>
      </TableCell>
      <TableCell>
        {item.child?.length > 1 ? (
          <ul className={historyCls.liItem}>
            {item.child.map((note, index) => (
              <li key={index}>
                <Text className={historyCls.label}>{note.split('_').join(' ')}</Text>
              </li>
            ))}
          </ul>
        ) : (
          <Text className={historyCls.label}>{item.child?.[0]}</Text>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <div className={classes.headingWithBtnWrapper}>
        <div className={classes.noteHeaderLeftSide}>
          <Heading className={classes.heading} level={headingLevel.XL} weight={fontWeight.BOLD}>
            History
          </Heading>
          {false && (
            <Badge variant={badgeType.OUTLINED} style={badgeStyle.UNRELATED} className={classes.totalBadge}>
              {21} total
            </Badge>
          )}
        </div>
      </div>
      {items?.length > 0 || isLoading || error ? (
        <div className={clsx(classes.noteCardWrapper, historyCls.historyContainer)}>
          <Table>
            <TableBody>
              {isLoading || error ? (
                <TableRow className={historyCls.row}>
                  <TableCell colSpan={2} sx={{ textAlign: 'center' }}>
                    {error ? (
                      <Text className={historyCls.value}>{error}</Text>
                    ) : (
                      <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                items.map(renderContent)
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        <EmptyState
          title="No medical history yet"
          subtitle={`${demographicDetails.nickName} doesn’t have any medical history yet. This should appear after their first evaluation.`}
          glyph="face-talking-amazed"
        />
      )}
    </>
  );
});

History.displayName = 'History';

export { History };
